import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import type { ArticleKind } from '@nagoya-cat/shared';
import { api } from '../api/client';
import type { SeoState } from '../components/SeoPanel';

export interface EditorState {
  kind: ArticleKind;
  title: string;
  excerpt: string;
  status: string;
  scheduledAt: number | null;
  bodyJson: string | null;
  seo: SeoState;
  relatedIds: string[];
  categoryIds: string[];
  coverMediaId: string | null;
  coverUrl: string | null;
  /** 頁面（kind=page）專用：父頁面與選單排序。 */
  parentId: string | null;
  menuOrder: number | null;
}

const EMPTY: EditorState = {
  kind: 'post',
  title: '',
  excerpt: '',
  status: 'draft',
  scheduledAt: null,
  bodyJson: null,
  seo: {},
  relatedIds: [],
  categoryIds: [],
  coverMediaId: null,
  coverUrl: null,
  parentId: null,
  menuOrder: null,
};

/** 載入文章/頁面詳情、保存編輯狀態、儲存 mutation。 */
export function useArticleEditor(id: string) {
  const qc = useQueryClient();
  const [state, setState] = useState<EditorState>(EMPTY);

  const detail = useQuery({ queryKey: ['article', id], queryFn: () => api.getArticle(id) });

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    setState({
      kind: d.article.kind,
      title: d.article.title,
      excerpt: d.article.excerpt ?? '',
      status: d.article.status,
      scheduledAt: d.article.scheduledAt ?? null,
      bodyJson: d.article.bodyJson,
      seo: (d.seo ?? {}) as SeoState,
      relatedIds: d.relatedIds,
      categoryIds: d.categoryIds,
      coverMediaId: d.article.coverMediaId ?? null,
      coverUrl: d.coverUrl ?? null,
      parentId: d.article.parentId ?? null,
      menuOrder: d.article.menuOrder ?? null,
    });
  }, [detail.data]);

  const patch = (p: Partial<EditorState>) => setState((s) => ({ ...s, ...p }));

  const save = useMutation({
    mutationFn: () => {
      // 排程刊登需先選時間，否則先擋下，避免存了卻不會自動發佈。
      if (state.status === 'scheduled' && !state.scheduledAt) {
        return Promise.reject(new Error('請先選擇排程刊登時間'));
      }
      const isPage = state.kind === 'page';
      return api.saveArticle(id, {
        patch: {
          title: state.title,
          excerpt: state.excerpt,
          status: state.status,
          scheduledAt: state.scheduledAt,
          bodyJson: state.bodyJson,
          coverMediaId: state.coverMediaId,
          // 頁面用 parent/menuOrder；文章不送這兩欄。
          ...(isPage ? { parentId: state.parentId, menuOrder: state.menuOrder } : {}),
        },
        seo: state.seo as Record<string, unknown>,
        relatedIds: isPage ? [] : state.relatedIds,
        categoryIds: isPage ? [] : state.categoryIds,
      });
    },
    onSuccess: () => {
      const when =
        state.status === 'scheduled' && state.scheduledAt
          ? new Date(state.scheduledAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })
          : null;
      message.success(when ? `已排程，將於 ${when} 自動發佈` : '已儲存');
      qc.invalidateQueries({ queryKey: ['article', id] });
      qc.invalidateQueries({ queryKey: ['articles'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  return {
    state,
    patch,
    save,
    loading: detail.isLoading,
    legacyHtml: detail.data?.article.bodyHtmlLegacy ?? null,
  };
}
